import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import authService, { 
  AuthResponse, 
} from '../services/authService';
import profileService, { UserProfileDetails, UpdateUserProfileRequest } from '../services/profileService';
import secureStorage from '../utils/secureStorage';

interface AuthState {
  user: AuthResponse | null;
  profile: UserProfileDetails | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const unauthenticatedAuthState = (): AuthState => ({
  user: null,
  profile: null,
  isAuthenticated: false,
  loading: false,
  error: null,
});

const applyProfileDisplayNamesToUser = (state: AuthState, profile: UserProfileDetails): void => {
  if (!state.user) return;

  const firstName = String(profile.firstName || '').trim();
  const lastName = String(profile.lastName || '').trim();
  const nickName = String(profile.nickname || '').trim();
  if (!firstName && !lastName && !nickName) return;

  if (firstName) state.user.firstName = firstName;
  if (lastName) state.user.lastName = lastName;
  if (nickName) state.user.nickName = nickName;
  secureStorage.setUserData(state.user);
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
    const bufferCtor = typeof globalThis !== 'undefined' ? (globalThis as any).Buffer : undefined;
    const json =
      typeof atob === 'function'
        ? atob(padded)
        : bufferCtor
        ? bufferCtor.from(padded, 'base64').toString('utf8')
        : '';
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const isJwtTokenExpired = (token: string, nowMs = Date.now()): boolean => {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  return typeof exp === 'number' && exp * 1000 <= nowMs;
};

export const buildInitialAuthStateFromStorage = ({
  token,
  user,
  cachedProfile,
  clearStorage,
  nowMs = Date.now(),
}: {
  token: string | null;
  user: AuthResponse | null;
  cachedProfile: UserProfileDetails | null;
  clearStorage: () => void;
  nowMs?: number;
}): AuthState => {
  if (token && (!user || isJwtTokenExpired(token, nowMs))) {
    clearStorage();
    return unauthenticatedAuthState();
  }

  return {
    user,
    profile: cachedProfile,
    isAuthenticated: Boolean(token),
    loading: false,
    error: null,
  };
};

// Function to safely initialize auth state with token validation
const initializeAuthState = (): AuthState => {
  try {
    // Use authService methods which properly read from secureStorage
    const token = authService.getToken();
    const user = authService.getCurrentUser();
    const cachedProfile = secureStorage.getProfileData(); // Load minimal cached profile (firstName, lastName, nickname, avatar only)

    return buildInitialAuthStateFromStorage({
      token,
      user,
      cachedProfile, // Use minimal cached profile for instant avatar display (full profile fetched in background)
      clearStorage: () => secureStorage.clearAll(),
    });
  } catch (error) {
    // If there's any error during initialization, clear everything via authService
    secureStorage.clearAll();
    return unauthenticatedAuthState();
  }
};

const initialState: AuthState = initializeAuthState();

// Async thunks
export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    // Client logout should be resilient: even if the backend logout call fails,
    // `authService.logout()` clears storage in its `finally`, so we still want to
    // transition the UI into a logged-out state.
    try {
      await authService.logout();
    } catch (error) {
      // Best-effort only; ignore backend/network errors.
      console.warn('[auth] Logout request failed; proceeding with local logout:', error);
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileService.getUserProfile();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch profile');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (data: UpdateUserProfileRequest, { rejectWithValue }) => {
    try {
      const response = await profileService.updateUserProfile(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.profile = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setAuthenticatedUser: (state, action: PayloadAction<AuthResponse>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    setProfileAvatar: (state, action: PayloadAction<string | null>) => {
      if (state.profile) {
        state.profile.avatar = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
        // Clear auth state immediately to avoid stale UI (e.g. avatar) if the
        // network logout request fails or takes a long time.
        state.user = null;
        state.profile = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.profile = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        // Even if something unexpected causes a rejection, prefer a consistent
        // logged-out UI state.
        state.user = null;
        state.profile = null;
        state.isAuthenticated = false;
        state.error = (action.payload as string) || null;
      })
      // Fetch Profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action: PayloadAction<UserProfileDetails>) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
        applyProfileDisplayNamesToUser(state, action.payload);
        
        // Cache only display-critical fields (firstName, lastName, nickname, avatar) to localStorage
        secureStorage.setProfileData(action.payload);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        // Don't show auth errors for users who are just browsing
        const errorMessage = action.payload as string;
        if (errorMessage?.includes('Authentication required') || 
            errorMessage?.includes('401') ||
            errorMessage?.includes('获取用户信息异常') ||
            errorMessage?.includes('需要登录') ||
            errorMessage?.includes('认证失败')) {
          state.error = null; // Clear auth errors for browsing users
        } else {
          state.error = errorMessage;
        }
      })
      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action: PayloadAction<UserProfileDetails>) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
        applyProfileDisplayNamesToUser(state, action.payload);
        
        // Update the cache with the new profile data
        secureStorage.setProfileData(action.payload);
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearAuth, setAuthenticatedUser, setProfileAvatar } = authSlice.actions;
export default authSlice.reducer;
